import { UserScreenId } from "@vahak/core/dist/constants";
import {
    BANK_DETAILS,
    BULK_LOADS_LIST,
    DASHBOARD_ROUTE,
    HOME_ROUTE,
    LISTING_PAGE,
    CLIENT_LISTING_PAGE,
    MARKETPLACE_ROUTE,
    MEMBERSHIP,
    MEMBERSHIP_HISTORY,
    MEMBERSHIP_UPGRADE,
    MY_LORRIES_PAGE,
    PAYMENT_LIST,
    PREMIUM_LOAD_TRIP,
    TRANSPORT_DIRECTORY,
    TRANSPORT_NAGAR,
    USER_PROFILE_PAGE
} from "../constants/route-constants";

/** TopNavV2 labels (Find Loads, profile menu, app download banner). */
export const WEB_V2_NAV_LOCALIZATION_NAMESPACES = ["navigation", "common"] as const;

/** Compact post-load sheet (opened from side nav, dashboard, marketplace, etc.). */
export const WEB_V2_POSTING_LOCALIZATION_NAMESPACES = [
    "load-form",
    "homepageV2",
    "mktCardV2",
    "navigation",
    "common"
] as const;

/** LCV route SEO landing pages — hero CTAs open the compact post-load sheet. */
export const LCV_ROUTE_BOOKING_LOCALIZATION_NAMESPACES = [
    "lcvRouteLanding",
    ...WEB_V2_POSTING_LOCALIZATION_NAMESPACES
] as const;

/** Compact bid sheet */
export const WEB_V2_BIDDING_LOCALIZATION_NAMESPACES = ["load-form", "mktCardV2", "homepageV2"] as const;

/** Nav + posting — load client-side on any route that can open post flow or TopNavV2. */
export const WEB_V2_SHELL_LOCALIZATION_NAMESPACES = [
    ...WEB_V2_NAV_LOCALIZATION_NAMESPACES,
    ...WEB_V2_POSTING_LOCALIZATION_NAMESPACES
] as const;

/** Logged-in shell: mobile TopNavV2 + side-nav post load + compact flows. */
export const LOGGED_IN_APP_LOCALIZATION_NAMESPACES = [
    ...WEB_V2_NAV_LOCALIZATION_NAMESPACES,
    ...WEB_V2_POSTING_LOCALIZATION_NAMESPACES
] as const;

/** Homepage + compact bid/post + top nav + shared cards */
export const HOME_LOCALIZATION_NAMESPACES = ["homepageV2", "mktCardV2", "load-form", "navigation", "common"] as const;

/** Marketplace V3 + shared homepage-v2 / LCV landing surfaces */
export const MARKETPLACE_V3_LOCALIZATION_NAMESPACES = [
    "marketplaceV3",
    "lcvRouteLanding",
    "homepageV2",
    "mktCardV2",
    "load-form",
    "navigation",
    "common"
] as const;

/** Public upgrade page + compact post-load sheet opened from TopNavV2 / side nav. */
export const MEMBERSHIP_UPGRADE_LOCALIZATION_NAMESPACES = [
    "membershipPage",
    ...WEB_V2_POSTING_LOCALIZATION_NAMESPACES
] as const;

/** Logged-in dashboard home + TopNavV2 + compact post-load flow */
export const DASHBOARD_LOCALIZATION_NAMESPACES = ["homeRevamp", ...LOGGED_IN_APP_LOCALIZATION_NAMESPACES] as const;

/** My loads / lorries listing & bids */
export const LISTING_PAGE_LOCALIZATION_NAMESPACES = [
    "marketplace",
    "userProfile",
    ...LOGGED_IN_APP_LOCALIZATION_NAMESPACES
] as const;

export const USER_PROFILE_LOCALIZATION_NAMESPACES = ["userProfile", ...LOGGED_IN_APP_LOCALIZATION_NAMESPACES] as const;

export const BANK_DETAILS_LOCALIZATION_NAMESPACES = [...LOGGED_IN_APP_LOCALIZATION_NAMESPACES] as const;

export const MEMBERSHIP_PLAN_LOCALIZATION_NAMESPACES = ["membershipPage", "navigation", "common"] as const;

export const TRANSPORT_DIRECTORY_LOCALIZATION_NAMESPACES = [
    "transporters",
    ...WEB_V2_NAV_LOCALIZATION_NAMESPACES
] as const;

export const TRANSPORTER_PROFILE_LOCALIZATION_NAMESPACES = [
    "userProfile",
    "marketplace",
    "transporters",
    ...WEB_V2_NAV_LOCALIZATION_NAMESPACES
] as const;

export const TRANSPORT_NAGAR_LOCALIZATION_NAMESPACES = [
    "common",
    "mktCardV2",
    ...LOGGED_IN_APP_LOCALIZATION_NAMESPACES
] as const;

export const SERVICE_PAGE_LOCALIZATION_NAMESPACES = ["servicePages", ...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

export const WHY_CHOOSE_US_LOCALIZATION_NAMESPACES = ["whyChooseUs", ...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

export const HOW_VAHAK_HELPS_LOCALIZATION_NAMESPACES = [
    "howVahakHelpYou",
    ...WEB_V2_NAV_LOCALIZATION_NAMESPACES
] as const;

/** City SEO landing pages — hero CTAs open the compact post-load sheet. */
export const CITY_PAGE_LOCALIZATION_NAMESPACES = [
    "lcvRouteLanding",
    ...WEB_V2_POSTING_LOCALIZATION_NAMESPACES
] as const;

export const FAQ_LOCALIZATION_NAMESPACES = ["faq", ...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

export const CONTACT_US_LOCALIZATION_NAMESPACES = ["contactus", ...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

export const CAREERS_LOCALIZATION_NAMESPACES = [...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

export const ABOUT_US_LOCALIZATION_NAMESPACES = [...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

export const PREMIUM_LOCALIZATION_NAMESPACES = ["marketplace", ...LOGGED_IN_APP_LOCALIZATION_NAMESPACES] as const;

export const PAYMENT_LOCALIZATION_NAMESPACES = [...LOGGED_IN_APP_LOCALIZATION_NAMESPACES] as const;

export const HELP_LOCALIZATION_NAMESPACES = ["help", "common"] as const;

/** Legal/static pages with TopNavV2 only (content is mostly hardcoded). */
export const STATIC_PAGE_LOCALIZATION_NAMESPACES = [...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

/** Public VAS pages with TopNavV2 + compact post-load sheet (no page-specific i18n file). */
export const VAS_STATIC_LOCALIZATION_NAMESPACES = [...WEB_V2_POSTING_LOCALIZATION_NAMESPACES] as const;

/** Cargo insurance — page copy + compact post-load sheet opened from TopNavV2. */
export const CARGO_INSURANCE_LOCALIZATION_NAMESPACES = [
    "cargoInsurance",
    ...WEB_V2_POSTING_LOCALIZATION_NAMESPACES
] as const;

/**
 * Maps a route pathname or layout route id to the i18n namespace files
 * required by `serverSideTranslations`.
 */
export const ROUTE_LOCALIZATION_NAMESPACES = {
    "/": HOME_LOCALIZATION_NAMESPACES,
    [HOME_ROUTE]: HOME_LOCALIZATION_NAMESPACES,
    [MARKETPLACE_ROUTE]: MARKETPLACE_V3_LOCALIZATION_NAMESPACES,
    "/membership/upgrade": MEMBERSHIP_UPGRADE_LOCALIZATION_NAMESPACES,
    [MEMBERSHIP_UPGRADE]: MEMBERSHIP_UPGRADE_LOCALIZATION_NAMESPACES,
    [DASHBOARD_ROUTE]: DASHBOARD_LOCALIZATION_NAMESPACES,
    "/dashboard": DASHBOARD_LOCALIZATION_NAMESPACES,
    [LISTING_PAGE]: LISTING_PAGE_LOCALIZATION_NAMESPACES,
    [BANK_DETAILS]: BANK_DETAILS_LOCALIZATION_NAMESPACES,
    [MEMBERSHIP]: MEMBERSHIP_PLAN_LOCALIZATION_NAMESPACES,
    [MEMBERSHIP_HISTORY]: MEMBERSHIP_PLAN_LOCALIZATION_NAMESPACES,
    [MY_LORRIES_PAGE]: USER_PROFILE_LOCALIZATION_NAMESPACES,
    new: USER_PROFILE_LOCALIZATION_NAMESPACES,
    [USER_PROFILE_PAGE]: USER_PROFILE_LOCALIZATION_NAMESPACES,
    [TRANSPORT_DIRECTORY]: TRANSPORT_DIRECTORY_LOCALIZATION_NAMESPACES,
    transporterProfile: TRANSPORTER_PROFILE_LOCALIZATION_NAMESPACES,
    [TRANSPORT_NAGAR]: TRANSPORT_NAGAR_LOCALIZATION_NAMESPACES,
    servicePages: SERVICE_PAGE_LOCALIZATION_NAMESPACES,
    whyChooseUs: WHY_CHOOSE_US_LOCALIZATION_NAMESPACES,
    howVahakHelpYou: HOW_VAHAK_HELPS_LOCALIZATION_NAMESPACES,
    cityPages: CITY_PAGE_LOCALIZATION_NAMESPACES,
    faq: FAQ_LOCALIZATION_NAMESPACES,
    contactus: CONTACT_US_LOCALIZATION_NAMESPACES,
    careers: CAREERS_LOCALIZATION_NAMESPACES,
    aboutUs: ABOUT_US_LOCALIZATION_NAMESPACES,
    premium: PREMIUM_LOCALIZATION_NAMESPACES,
    [BULK_LOADS_LIST]: PREMIUM_LOCALIZATION_NAMESPACES,
    [PREMIUM_LOAD_TRIP]: PREMIUM_LOCALIZATION_NAMESPACES,
    [PAYMENT_LIST]: PREMIUM_LOCALIZATION_NAMESPACES,
    [UserScreenId.payment.id]: PAYMENT_LOCALIZATION_NAMESPACES,
    help: HELP_LOCALIZATION_NAMESPACES,
    staticPage: STATIC_PAGE_LOCALIZATION_NAMESPACES,
    [UserScreenId.cargoInsurance.pathname]: CARGO_INSURANCE_LOCALIZATION_NAMESPACES,
    [UserScreenId.cargoInsurance.id]: CARGO_INSURANCE_LOCALIZATION_NAMESPACES,
    "/vas/commercial-vehicle-insurance": VAS_STATIC_LOCALIZATION_NAMESPACES,
    [UserScreenId.lcvBooking.pathname]: LCV_ROUTE_BOOKING_LOCALIZATION_NAMESPACES,
    [UserScreenId.lcvBooking.id]: LCV_ROUTE_BOOKING_LOCALIZATION_NAMESPACES,
    [CLIENT_LISTING_PAGE]: LISTING_PAGE_LOCALIZATION_NAMESPACES,
    [UserScreenId.listing.pathname]: LISTING_PAGE_LOCALIZATION_NAMESPACES
} as const;

export type RouteLocalizationKey = keyof typeof ROUTE_LOCALIZATION_NAMESPACES;

const DEFAULT_LOCALIZATION_NAMESPACES = [...WEB_V2_NAV_LOCALIZATION_NAMESPACES] as const;

export function getRouteLocalizationNamespaces(route: string): string[] {
    const namespaces = ROUTE_LOCALIZATION_NAMESPACES[route as RouteLocalizationKey];

    if (namespaces) {
        return [...namespaces];
    }

    return [...DEFAULT_LOCALIZATION_NAMESPACES];
}
